import React, { useState, useEffect } from 'react';
import './Demo.css';
import VoiceVisualizer from './VoiceVisualizer';
import { AgentStatus } from '../types';
import { geminiClient } from '../services/geminiService';
import Chatbot from './Chatbot';
import CalinChatbot from './CalinChatbot';

const Demo = () => {
  const [activeTab, setActiveTab] = useState('voice'); // 'voice' or 'chatbot'
  const [status, setStatus] = useState(AgentStatus.DISCONNECTED);
  const [amplitude, setAmplitude] = useState(0);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  useEffect(() => {
    // Set up callbacks
    geminiClient.onStatusChange = (newStatus) => {
      setStatus(newStatus);
    };

    geminiClient.onAudioData = (amp) => {
      setAmplitude(amp);
    };

    geminiClient.onLogMessage = (source, text) => {
      console.log(`[${source}]: ${text}`);
    };

    geminiClient.onToolCall = (name, args) => {
      console.log(`Tool called: ${name}`, args);
    };

    // Cleanup on unmount
    return () => {
      geminiClient.disconnect();
    };
  }, []);

  const handleConnect = async () => {
    if (status === AgentStatus.DISCONNECTED) {
      await geminiClient.connect();
    } else {
      geminiClient.disconnect();
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'disconnected': 'Ready to Connect',
      'connecting': 'Connecting...',
      'listening': 'Listening',
      'speaking': 'Calin is Speaking',
      'thinking': 'Processing...',
      'error': 'Connection Error'
    };
    return labels[status] || status;
  };

  return (
    <div className="demo-page">
      <div className="demo-hero">
        <div className="container">
          <h1>Experience Our AI Solutions</h1>
          <p>Try Calin, our AI receptionist, or chat with our intelligent chatbot</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="demo-tabs">
        <div className="container">
          <button
            className={`demo-tab ${activeTab === 'voice' ? 'active' : ''}`}
            onClick={() => setActiveTab('voice')}
          >
            <i className="fas fa-phone"></i>
            Voice AI - Calin
          </button>
          <button
            className={`demo-tab ${activeTab === 'chatbot' ? 'active' : ''}`}
            onClick={() => setActiveTab('chatbot')}
          >
            <i className="fas fa-comments"></i>
            Chatbot Demo
          </button>
        </div>
      </div>

      <div className="demo-content">
        <div className="container">
          {/* Voice AI Demo */}
          {activeTab === 'voice' && (
            <div className="demo-section">
              <div className="demo-intro">
                <h2>Meet Calin - Your AI Receptionist</h2>
                <p>Intelligent voice assistant that handles calls, schedules appointments, and provides 24/7 customer support.</p>
              </div>

              <div className="demo-compact-card">
                <div className="voice-demo-left">
                  <div className="voice-visualizer-compact">
                    <div className="visualizer-wrapper">
                      <VoiceVisualizer 
                        amplitude={amplitude} 
                        isActive={status !== AgentStatus.DISCONNECTED} 
                        status={status} 
                      />
                    </div>
                    <div className="demo-agent-name">
                      <i className="fas fa-microphone"></i>
                      <span>Calin</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleConnect}
                    className={`voice-play-button ${status === AgentStatus.DISCONNECTED ? 'play' : 'stop'}`}
                    disabled={status === 'connecting' || status === 'thinking'}
                  >
                    {status === AgentStatus.DISCONNECTED ? (
                      <>
                        <i className="fas fa-play"></i>
                        <span>Start Call</span>
                      </>
                    ) : status === 'connecting' ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-stop"></i>
                        <span>End Call</span>
                      </>
                    )}
                  </button>
                  
                  <p className="demo-status-compact">
                    <span className="demo-status-value">{getStatusLabel(status)}</span>
                  </p>
                </div>

                <div className="voice-demo-right">
                </div>
              </div>

              {/* Business Advantages */}
              <div className="demo-benefits">
                <h3>Business Advantages</h3>
                <div className="benefits-grid">
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <h4>24/7 Availability</h4>
                    <p>Never miss a call. Calin works around the clock, handling customer inquiries even after hours.</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <i className="fas fa-dollar-sign"></i>
                    </div>
                    <h4>Save 50%+ on Costs</h4>
                    <p>Reduce staffing costs significantly while providing better, faster customer service.</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <i className="fas fa-rocket"></i>
                    </div>
                    <h4>Quick Setup</h4>
                    <p>Get Calin up and running in days, not months. No lengthy training or onboarding required.</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <i className="fas fa-chart-line"></i>
                    </div>
                    <h4>Scalable</h4>
                    <p>Handle unlimited calls simultaneously. Scale up instantly during peak times.</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <i className="fas fa-smile"></i>
                    </div>
                    <h4>Consistent Service</h4>
                    <p>Every customer gets the same high-quality experience, every time.</p>
                  </div>
                  <div className="benefit-card">
                    <div className="benefit-icon">
                      <i className="fas fa-tasks"></i>
                    </div>
                    <h4>Multi-Tasking</h4>
                    <p>Calin can handle scheduling, FAQs, transfers, and more - all at once.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Chatbot Demo */}
          {activeTab === 'chatbot' && (
            <div className="demo-section">
              <div className="demo-intro">
                <h2>Calin Chatbot Demo</h2>
                <p>Experience Calin as a text-based chatbot. Chat with Calin to schedule appointments, get service information, and receive instant support.</p>
              </div>

              <div className="chatbot-demo-layout">
                <div className="chatbot-embedded">
                  <CalinChatbot
                    isOpen={true}
                    onClose={null}
                  />
                </div>
                
                <div className="chatbot-info-sidebar">
                  <h3>Chatbot Features</h3>
                  <div className="chatbot-features-list">
                    <div className="feature-item">
                      <i className="fas fa-check-circle"></i>
                      <span>Instant responses</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-check-circle"></i>
                      <span>Website navigation</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-check-circle"></i>
                      <span>Product information</span>
                    </div>
                    <div className="feature-item">
                      <i className="fas fa-check-circle"></i>
                      <span>Lead generation</span>
                    </div>
                  </div>
                  
                  <div className="chatbot-benefits-compact">
                    <h4>Benefits</h4>
                    <div className="benefits-mini">
                      <div className="benefit-mini">
                        <i className="fas fa-bolt"></i>
                        <span>Instant Support</span>
                      </div>
                      <div className="benefit-mini">
                        <i className="fas fa-globe"></i>
                        <span>Easy Integration</span>
                      </div>
                      <div className="benefit-mini">
                        <i className="fas fa-users"></i>
                        <span>Lead Capture</span>
                      </div>
                      <div className="benefit-mini">
                        <i className="fas fa-language"></i>
                        <span>Multi-Language</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chatbot Component - Only show when not in chatbot tab */}
      {activeTab !== 'chatbot' && (
        <Chatbot
          isOpen={isChatbotOpen}
          onClose={() => setIsChatbotOpen(false)}
        />
      )}
    </div>
  );
};

export default Demo;
